import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';

export const ID23_MODELO_TITULO_MARKER = 'ID-23';

export const ID23_SOPORTACONO_OPTIONS = ['Si', 'No'] as const;
export const ID23_MAQUINA_OPTIONS = ['E-25', 'V-25', 'P-50', 'S-14', 'LB-100', 'COEX', 'RF-5', 'R-25'] as const;
export const ID23_COLOR_FUNDA_OPTIONS = [
  'Natural',
  'Amarillo',
  'Azul',
  'Rojo',
  'Verde',
  'Negro',
  'Naranjo',
  'Rosado',
  'Café',
  'Celeste',
  'Cartón corrugado',
] as const;
export const ID23_MICROPERFORADO_OPTIONS = ['Grueso', 'Fino', 'N/A'] as const;
export const ID23_PRODUCTO_A_FABRICAR_OPTIONS = ['MANGA CERRADA', 'MANGA L', 'FILM'] as const;

export const toMedicionSelectOptions = (catalog: readonly string[], current?: string | number | null) => {
  const options = catalog.map((value) => ({value, label: value}));
  if (current === null || current === undefined || current === '') return options;
  const currentStr = String(current);
  if (!catalog.includes(currentStr)) {
    return [{value: currentStr, label: currentStr}, ...options];
  }
  return options;
};

export type MedicionesParcialId23 = {
  termocontraccion_t?: number | string | null;
  termocontraccion_l?: number | string | null;
  cof_interno_estatico?: number | string | null;
  cof_interno_dinamico?: number | string | null;
  cof_externo_estatico?: number | string | null;
  cof_externo_dinamico?: number | string | null;
  tension_bobinado_inicio?: number | string | null;
  tension_bobinado_termino?: number | string | null;
  corriente_estatica_inicio?: number | string | null;
  corriente_estatica_termino?: number | string | null;
  producto_a_fabricar?: string | null;
  maquina?: string | null;
  color_funda?: string | null;
  microperforado?: string | null;
  peso_rollo?: number | string | null;
  soportacono?: string | null;
  na_termocontraccion?: boolean | null;
  na_cof?: boolean | null;
  na_tension_bobinado?: boolean | null;
  na_corriente_estatica?: boolean | null;
};

export const emptyMedicionesParcialId23 = (): MedicionesParcialId23 => ({
  termocontraccion_t: null,
  termocontraccion_l: null,
  cof_interno_estatico: null,
  cof_interno_dinamico: null,
  cof_externo_estatico: null,
  cof_externo_dinamico: null,
  tension_bobinado_inicio: null,
  tension_bobinado_termino: null,
  corriente_estatica_inicio: null,
  corriente_estatica_termino: null,
  producto_a_fabricar: null,
  maquina: null,
  color_funda: null,
  microperforado: null,
  peso_rollo: null,
  soportacono: null,
  na_termocontraccion: false,
  na_cof: false,
  na_tension_bobinado: false,
  na_corriente_estatica: false,
});

export const isId23ModeloTareaTitle = (titulo?: string | null) =>
  String(titulo || '')
    .toUpperCase()
    .includes(ID23_MODELO_TITULO_MARKER);

export const shouldUseMedicionesParcialId23 = (params: {
  tenantId?: string | null;
  modeloTareaTitulo?: string | null;
  primeroDelParcial?: boolean | null;
}) =>
  Boolean(isPlastyvergTenant(params.tenantId) && isId23ModeloTareaTitle(params.modeloTareaTitulo) && params.primeroDelParcial);

export const shouldMarkPrimeroDelParcialOnCreate = (params: {
  tenantId?: string | null;
  modeloTareaTitulo?: string | null;
  hasUnverifiedChildren: boolean;
}) => {
  const titulo = String(params.modeloTareaTitulo || '').toUpperCase();
  const isMedicionesModelo = titulo.includes(ID23_MODELO_TITULO_MARKER) || titulo.includes('ID-24');
  return Boolean(isPlastyvergTenant(params.tenantId) && isMedicionesModelo && !params.hasUnverifiedChildren);
};

/** ID-23/ID-24 de Plastyverg marcadas como primeroDelParcial. El resto no debe persistir medicionesParcial. */
export const shouldPersistMedicionesParcial = (params: {
  tenantId?: string | null;
  modeloTareaTitulo?: string | null;
  primeroDelParcial?: boolean | null;
}) => {
  const titulo = String(params.modeloTareaTitulo || '').toUpperCase();
  const isMedicionesModelo = titulo.includes(ID23_MODELO_TITULO_MARKER) || titulo.includes('ID-24');
  return Boolean(isPlastyvergTenant(params.tenantId) && isMedicionesModelo && params.primeroDelParcial);
};

export const omitMedicionesParcialIfUnused = (
  data: Record<string, any> | null | undefined,
  params: {
    tenantId?: string | null;
    modeloTareaTitulo?: string | null;
    primeroDelParcial?: boolean | null;
  },
) => {
  if (!data) return data;
  if (
    !shouldPersistMedicionesParcial({
      tenantId: params.tenantId,
      modeloTareaTitulo: params.modeloTareaTitulo || data.modeloTarea?.titulo || data.tituloTarea,
      primeroDelParcial: params.primeroDelParcial ?? data.primeroDelParcial,
    })
  ) {
    delete data.medicionesParcial;
  }
  return data;
};
export const normalizeMedicionesParcialId23 = (value?: MedicionesParcialId23 | null): MedicionesParcialId23 => ({
  ...emptyMedicionesParcialId23(),
  ...(value || {}),
});

export const isMedicionValueFilled = (value: unknown) => value !== null && value !== undefined && String(value).trim() !== '';

export type RequiredMedicionBlock = {
  labelKey: string;
  naKey?: string;
  keys?: readonly string[];
  arrayKey?: string;
};

export const isMedicionBlockNotApplicable = (mediciones: Record<string, any> | null | undefined, naKey?: string) =>
  Boolean(naKey && mediciones?.[naKey]);

export const isRequiredMedicionBlockFilled = (
  mediciones: Record<string, any> | null | undefined,
  block: RequiredMedicionBlock,
) => {
  if (isMedicionBlockNotApplicable(mediciones, block.naKey)) return true;
  if (block.arrayKey) {
    const selected = mediciones?.[block.arrayKey];
    return Array.isArray(selected) && selected.length > 0;
  }
  return (block.keys || []).every((key) => {
    if (key === 'troquel') {
      return isMedicionValueFilled(mediciones?.troquel ?? mediciones?.troquel_cm);
    }
    return isMedicionValueFilled(mediciones?.[key]);
  });
};

export const getMissingRequiredMedicionBlockLabels = (
  mediciones: Record<string, any> | null | undefined,
  blocks: readonly RequiredMedicionBlock[],
  translate: (key: string) => string,
) => blocks.filter((block) => !isRequiredMedicionBlockFilled(mediciones, block)).map((block) => translate(block.labelKey));

export const ID23_REQUIRED_MEDICION_BLOCKS: readonly RequiredMedicionBlock[] = [
  {
    labelKey: 'entities.tarea.medicionesParcial.termocontraccion',
    naKey: 'na_termocontraccion',
    keys: ['termocontraccion_t', 'termocontraccion_l'],
  },
  {
    labelKey: 'entities.tarea.medicionesParcial.cof',
    naKey: 'na_cof',
    keys: ['cof_interno_estatico', 'cof_interno_dinamico', 'cof_externo_estatico', 'cof_externo_dinamico'],
  },
  {
    labelKey: 'entities.tarea.medicionesParcial.tensionBobinado',
    naKey: 'na_tension_bobinado',
    keys: ['tension_bobinado_inicio', 'tension_bobinado_termino'],
  },
  {
    labelKey: 'entities.tarea.medicionesParcial.corrienteEstatica',
    naKey: 'na_corriente_estatica',
    keys: ['corriente_estatica_inicio', 'corriente_estatica_termino'],
  },
  {labelKey: 'entities.tarea.medicionesParcial.maquina', keys: ['maquina']},
  {labelKey: 'entities.tarea.medicionesParcial.productoAFabricar', keys: ['producto_a_fabricar']},
  {labelKey: 'entities.tarea.medicionesParcial.colorFunda', keys: ['color_funda']},
  {labelKey: 'entities.tarea.medicionesParcial.microperforado', keys: ['microperforado']},
  {labelKey: 'entities.tarea.medicionesParcial.pesoRollo', keys: ['peso_rollo']},
  {labelKey: 'entities.tarea.medicionesParcial.soportaconos', keys: ['soportacono']},
];

export const getMissingRequiredMedicionesParcialId23Labels = (
  mediciones: MedicionesParcialId23 | null | undefined,
  translate: (key: string) => string,
) => getMissingRequiredMedicionBlockLabels(mediciones, ID23_REQUIRED_MEDICION_BLOCKS, translate);

export const formatMedicionesParcialValue = (value: unknown, notApplicable?: boolean) => {
  if (notApplicable) return 'N/A';
  if (value === null || value === undefined || value === '') return 'N/A';
  return String(value);
};

const isId23PrimeroDelParcialTask = (task: any) =>
  task?.primeroDelParcial === true && isId23ModeloTareaTitle(task?.modeloTarea?.titulo || task?.tituloTarea || task?.titulo);

export const hasTermocontraccionData = (mediciones?: MedicionesParcialId23 | null) => {
  if (mediciones?.na_termocontraccion) return true;
  const t = mediciones?.termocontraccion_t;
  const l = mediciones?.termocontraccion_l;
  return (t !== null && t !== undefined && t !== '') || (l !== null && l !== undefined && l !== '');
};

export const findPrimeroDelParcialId23Task = (tasks: any[] = []) => tasks.find(isId23PrimeroDelParcialTask) || null;

/** Último primeroDelParcial ID-23 que tenga T/L de termocontracción (útil con varios parciales en un PDF). */
export const findLastTermocontraccionPrimeroDelParcialId23Task = (tasks: any[] = []) => {
  for (let index = tasks.length - 1; index >= 0; index -= 1) {
    const task = tasks[index];
    if (isId23PrimeroDelParcialTask(task) && hasTermocontraccionData(task?.medicionesParcial)) {
      return task;
    }
  }
  return null;
};
